import React, { useState, useCallback, useEffect } from "react";
import { generateDistroSummaryKind } from "../../../utils/adminPanel.utils";
import { GroupByConfig, ReportFilterConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { SummaryTable } from "@aidkitorg/types/lib/survey";
import { BooleanExprToFilterGroupConverter, FilterGroupToBooleanExprConverter } from "../../shared/utils/filterConverters";

export type SummaryTableGroup = {
  title: string,
  dataField: string,
}

export const useSummaryTableFormState = (component?: SummaryTable) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const [groupByConfig, setGroupByConfig] = useState<GroupByConfig>({
    enabled: false,
    rules: []
  })

  const [filterConfig, setFilterConfig] = useState<ReportFilterConfig | undefined>(undefined);

  React.useEffect(() => {
    if (!component) return

    if (component.title) {
      const titleString = typeof component.title === 'string' ? component.title : component.title.en;
      setTitle(titleString);
    }
    if (component.description) {
      setDescription(component.description);
    }

    // Convert existing groups back to GroupByConfig format
    if (component.groups && component.groups.length > 0) {
      const rules = component.groups.map(group => ({
        field: group.kind === 'Field' ? group.field : '',
        direction: 'asc' as const
      })).filter(rule => rule.field !== '');

      if (rules.length > 0) {
        onGroupByChange({
          enabled: true,
          rules: rules
        });
      }
    }

    // Initialize filter config from component
    if (component.filter) {
      try {
        const convertedFilter = BooleanExprToFilterGroupConverter.convert(component.filter);
        if (convertedFilter) {
          setFilterConfig({ enabled: true, config: convertedFilter });
        }
      } catch (error) {
        console.warn('Failed to convert saved filter config:', error);
      }
    }
  }, [component])

  const onGroupByChange = (config: GroupByConfig) => {
    setGroupByConfig(config)
  }

  const buildSummaryTableObject = (): SummaryTable => {
    const summaryTable: SummaryTable = {
      kind: 'Summary Table',
      title: title,
      description: description,
      summary: [generateDistroSummaryKind('Count', '')], // Fixed to applicant count
      groups: groupByConfig.rules?.map(rule => ({
        kind: 'Field' as const,
        field: rule.field
      })) || []
    }

    // Add filter if it's configured
    if (filterConfig && filterConfig.enabled && filterConfig.config) {
      const booleanExpr = FilterGroupToBooleanExprConverter.convertFromConfig(filterConfig);
      if (booleanExpr) {
        summaryTable.filter = booleanExpr;
      }
    }

    return summaryTable
  }

  const isFormStateValid = 
    title !== '' &&
    groupByConfig.enabled &&
    groupByConfig.rules && 
    groupByConfig.rules.length > 0

  return {
    title, setTitle,
    description, setDescription,
    groupByConfig,
    onGroupByChange,
    filterConfig, setFilterConfig,
    buildSummaryTableObject,
    isFormStateValid,
  }
}